import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import FloatingCTA from '../../../components/feature/FloatingCTA';
import { faqs } from '../../../mocks/faqData';

export default function ChimneyCleaningPage() {
  const [openId, setOpenId] = useState<string | null>(null);
  const chimneyFaqs = faqs.filter((f) => f.category === 'chimney');

  return (
    <>
      <Header />
      <FloatingCTA />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/ee709c04ce224e904d44f37955a35a4c.jpg"
            alt="Chimney cleaning Jacksonville FL - BBM Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/90 via-brand-dark/70 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-brand-amber text-xs font-bold uppercase tracking-widest mb-5">
              <div className="h-px w-8 bg-brand-amber" />
              Chimney Cleaning Jacksonville FL
            </div>
            <h1 className="font-serif text-5xl md:text-6xl font-bold text-white mb-5 leading-tight">
              Professional Chimney Cleaning &amp; Inspection
            </h1>
            <p className="text-white/80 text-xl leading-relaxed mb-7">
              CSIA-certified chimney sweeps serving Jacksonville, FL. We remove creosote, inspect your flue, and make sure your fireplace is safe to use — all backed by a written inspection report.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="bg-brand-amber hover:bg-brand-amber-dark text-white font-bold py-4 px-8 rounded-lg text-base cursor-pointer whitespace-nowrap transition-all text-center">Book Chimney Cleaning</Link>
              <a href="tel:+19045550191" className="flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white/60 text-white font-semibold py-4 px-8 rounded-lg text-base cursor-pointer whitespace-nowrap transition-all">
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-phone-fill" /></div>
                (904) 555-0191
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What is chimney cleaning */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-14 items-start">
            <div className="lg:w-3/5">
              <h2 className="font-serif text-4xl font-bold text-brand-dark mb-5">What Is Chimney Cleaning?</h2>
              <p className="text-brand-warm-gray text-lg leading-relaxed mb-4">
                Chimney cleaning — also called a chimney sweep — is the process of removing creosote buildup, soot deposits, debris, and blockages from your chimney flue and firebox. Over time, burning wood creates a flammable residue called <strong>creosote</strong> that coats the interior walls of your chimney. Left uncleaned, creosote can ignite and cause a dangerous chimney fire.
              </p>
              <p className="text-brand-warm-gray text-base leading-relaxed mb-4">
                Professional chimney cleaning also includes a thorough inspection to identify cracks, structural damage, deteriorating mortar, improper caps, and other safety hazards that can allow carbon monoxide to enter your home.
              </p>
              <p className="text-brand-warm-gray text-base leading-relaxed mb-8">
                The National Fire Protection Association (NFPA) Standard 211 recommends that chimneys be inspected at least once a year and cleaned as needed. BBM Services performs Level I and Level II chimney inspections and cleanings for Jacksonville homeowners throughout the year.
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-brand-amber rounded-lg flex-shrink-0 mt-0.5">
                    <i className="ri-alarm-warning-fill text-white text-sm" />
                  </div>
                  <div>
                    <strong className="text-brand-dark text-sm">Did you know?</strong>
                    <p className="text-brand-warm-gray text-sm mt-1">Chimney fires burn at over 2,000°F and can cause severe structural damage to your home in seconds. Most are entirely preventable with annual cleaning.</p>
                  </div>
                </div>
              </div>

              <h3 className="font-serif text-2xl font-bold text-brand-dark mb-4">Who Needs Chimney Cleaning?</h3>
              <ul className="space-y-3 mb-8">
                {['Any homeowner who uses a wood-burning or gas fireplace', 'Jacksonville homes that haven\'t had a chimney inspection in over a year', 'Properties being bought or sold (Level II inspection required)', 'After any chimney fire, storm damage, or seismic event', 'When you notice smoke backing up into the room', 'If you smell a smoky odor when the fireplace is not in use'].map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-brand-light">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-check-line text-brand-amber" /></div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:w-2/5 space-y-5">
              {/* Pricing card */}
              <div className="bg-brand-dark rounded-2xl p-7 text-white">
                <h3 className="font-serif text-2xl font-bold mb-5">Chimney Cleaning Pricing</h3>
                <div className="space-y-4">
                  {[
                    { service: 'Level I Inspection + Cleaning', price: 'From $149' },
                    { service: 'Level II Inspection + Cleaning', price: 'From $249' },
                    { service: 'Fireplace / Firebox Cleaning', price: 'From $99' },
                    { service: 'Chimney Cap Installation', price: 'From $75' },
                  ].map((item) => (
                    <div key={item.service} className="flex items-center justify-between border-b border-white/10 pb-4 last:border-0 last:pb-0">
                      <span className="text-white/80 text-sm">{item.service}</span>
                      <span className="text-brand-amber font-bold text-sm whitespace-nowrap">{item.price}</span>
                    </div>
                  ))}
                </div>
                <Link to="/contact" className="block bg-brand-amber hover:bg-brand-amber-dark text-white font-bold py-3.5 text-center rounded-lg mt-6 cursor-pointer whitespace-nowrap transition-all text-sm">Get a Free Quote</Link>
              </div>

              {/* Features */}
              <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200">
                <h4 className="font-semibold text-brand-dark mb-4">What's Included</h4>
                {['Full chimney & flue inspection', 'Creosote and soot removal', 'Firebox cleaning', 'Damper inspection & lubrication', 'Chimney cap inspection', 'Before & after photos', 'Written inspection report'].map((f) => (
                  <div key={f} className="flex items-center gap-2 py-2 border-b border-stone-100 last:border-0">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0"><i className="ri-check-double-line text-brand-amber text-sm" /></div>
                    <span className="text-brand-light text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-brand-muted">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl font-bold text-brand-dark mb-8 text-center">Chimney Cleaning FAQs</h2>
          <div className="space-y-3">
            {chimneyFaqs.map((faq) => (
              <div key={faq.id} className={`border rounded-xl overflow-hidden transition-all ${openId === faq.id ? 'border-brand-amber/40 bg-amber-50/50' : 'border-stone-200 bg-white'}`}>
                <button className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left cursor-pointer" onClick={() => setOpenId(openId === faq.id ? null : faq.id)}>
                  <span className={`font-semibold text-sm leading-snug ${openId === faq.id ? 'text-brand-amber' : 'text-brand-dark'}`}>{faq.question}</span>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-colors ${openId === faq.id ? 'bg-brand-amber text-white' : 'bg-stone-100 text-brand-warm-gray'}`}>
                    <i className={`${openId === faq.id ? 'ri-subtract-line' : 'ri-add-line'} text-sm`} />
                  </div>
                </button>
                {openId === faq.id && <div className="px-6 pb-4"><p className="text-brand-warm-gray text-sm leading-relaxed">{faq.answer}</p></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-brand-amber">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-serif text-3xl font-bold text-white mb-3">Ready to Schedule Your Chimney Cleaning?</h2>
          <p className="text-white/80 text-lg mb-6">CSIA-certified technicians · Same-week availability · Jacksonville, FL</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-brand-amber font-bold py-3.5 px-8 rounded-lg text-base cursor-pointer whitespace-nowrap hover:bg-amber-50 transition-all">Book Chimney Cleaning</Link>
            <a href="tel:+19045550191" className="border-2 border-white/40 text-white font-semibold py-3.5 px-8 rounded-lg text-base cursor-pointer whitespace-nowrap hover:border-white transition-all">Call (904) 555-0191</a>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
